import { ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';

import { forkJoin, map, Observable, Subscription, switchMap } from 'rxjs';

import { ChartDisplayElementModel } from '../../../models/chart-display-element.model';
import { DataListChartComponent } from '../../../../Charts/components/DataListChart/data-list-chart.component';
import { Mention } from '../../../../Mentions/models/mention';
import { SearchCriteriaSortFieldEnum } from '../../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { SignificantDigitsPipe } from '../../../../Misc/pipes/SignificantDigits/significant-digits.pipe';
import { TopMentionsMetadataModel } from '../../../../Charts/models/top-mentions-metadata.model';
import { MentionContentCustomizationEnum } from '../../../../Mentions/enums/mention-content-customization.enum';
import { OutletsPublicApi } from '../../../../../api/targeting/outlets-public.api';
import { messageSeverityEnum } from '../../../../Misc/enums/message-severity.enum';
import { AgprComponentStyleEnum } from '../../../../Basic/enums/social-analysis-types.enum';

@Component({
    selector: 'agpr-top-mentions-chart',
    templateUrl: './top-mentions-chart.component.html',
    styleUrls: ['./top-mentions-chart.component.scss'],
    standalone: false
})
export class TopMentionsChartComponent extends DataListChartComponent implements OnInit, OnDestroy {

    @Input()
    set displayElement(displayElement: ChartDisplayElementModel) {
        this._displayElement = displayElement;
        this.metadata = new TopMentionsMetadataModel();
        this.displayElement.topMentionsMetadata.metadataTypes.forEach(type => this.metadata[type] = true);
        this.numToDisplay = displayElement.numOfItems;
        this.mentionSubscription?.unsubscribe();
        if (this.displayElement.topMentionsSortField === SearchCriteriaSortFieldEnum.custom) {
            // curated mentions are saved with the displayElement
            this.displayElement.series[0].mentionProvider.next(this.displayElement.topCuratedMentions);
        }
        this.mentionSubscription = this.displayElement.series[0].mentionProvider.pipe(
            map((data) => {
                this.mentions = data;
                if (this.mentions?.length > this.numToDisplay) {
                    this.mentions = this.mentions.slice(0, this.numToDisplay);
                }
            }),
            switchMap(() => {
                const outletRequests: Observable<any>[] = [];
                this.mentions?.forEach(mention => {
                    if (mention.publication.uuid) {
                        outletRequests.push(this.outletApi.getOutlet(mention.publication.uuid));
                    }
                });
                return forkJoin(outletRequests);
            }),
            map((outlets) => {
                this.outletPhotoMap = new Map<string, string>();
                outlets.forEach(outlet => {
                    if (outlet.avatarUrl) {
                        this.outletPhotoMap.set(outlet.id, outlet.avatarUrl);
                    }
                });
                this.displayElement.styles.outletPhotoMap = this.outletPhotoMap;
            })
        ).subscribe({
            next: () => {
                this.dataChange.emit(true);
            }
        });
    }
    get displayElement() {
        return this._displayElement;
    }
    @Input()
    set sortField(sortField: SearchCriteriaSortFieldEnum) {
        this._sortField = sortField;
    }
    get sortField() {
        return this._sortField;
    }

    @Output() dataChange = new EventEmitter<boolean>();

    _displayElement: ChartDisplayElementModel;
    _sortField: SearchCriteriaSortFieldEnum;
    mentions: Mention[] = [];
    outletPhotoMap: Map<string, string>;
    numToDisplay: number;
    metadata: TopMentionsMetadataModel;

    mentionSubscription: Subscription;

    readonly mentionContentTypes = MentionContentCustomizationEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;

    constructor(
        public elementRef: ElementRef,
        public decimalPipe: DecimalPipe,
        public currencyPipe: CurrencyPipe,
        public datePipe: DatePipe,
        public significantDigitsPipe: SignificantDigitsPipe,
        public zone: NgZone,
        public cdr: ChangeDetectorRef,
        public outletApi: OutletsPublicApi
    ) {
        super(
            elementRef,
            decimalPipe,
            currencyPipe,
            datePipe,
            significantDigitsPipe,
            zone
        );
    }

    ngOnInit(): void {
        this.series = this.displayElement.series;
    }

    ngOnDestroy(): void {
        this.mentionSubscription?.unsubscribe();
    }
}
